import { useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { AlertTriangle, Clock, Loader2, Pill, Siren, Skull } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { casesApi, type DecisionRead, type EncounterRead } from "@/features/cases/cases.api";
import { ApiError } from "@/lib/api";

const QUICK_ACTIONS = [
  "Nitroglicerina SL 0.4mg",
  "Aspirina 300 mg VO",
  "Tenecteplasa 0.5 mg/kg EV (trombólisis)",
  "Insulina regular 0.1 UI/kg/h EV",
  "SF 0.9% 500 ml bolo EV",
  "HBPM 1 mg/kg SC",
  "Oxigenoterapia 2 L/min",
];

export function TherapeuticPanel({
  encounterId,
  disabled,
  onUpdate,
}: {
  encounterId: string;
  disabled: boolean;
  onUpdate: (e: EncounterRead) => void;
}) {
  const qc = useQueryClient();
  const [intervention, setIntervention] = useState("");
  const [details, setDetails] = useState("");
  const [reasoning, setReasoning] = useState("");
  const [lastDecision, setLastDecision] = useState<DecisionRead | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      casesApi.therapeutic(encounterId, intervention, details || undefined, reasoning || undefined),
    onSuccess: async (d) => {
      setLastDecision(d);
      setIntervention("");
      setDetails("");
      setReasoning("");
      const enc = await casesApi.getEncounter(encounterId);
      onUpdate(enc);
      qc.invalidateQueries({ queryKey: ["encounter", encounterId] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Error al aplicar tratamiento"),
  });

  const advanceMut = useMutation({
    mutationFn: (m: number) => casesApi.forceAdvance(encounterId, m),
    onSuccess: (e) => {
      onUpdate(e);
      qc.invalidateQueries({ queryKey: ["encounter", encounterId] });
      toast.success("Tiempo simulado avanzado");
    },
  });

  const ev = lastDecision?.evaluation ?? {};
  const adverse = (ev.adverse_effects as Array<Record<string, unknown>> | undefined) ?? [];
  const resulting = (ev.resulting_events as Array<Record<string, unknown>> | undefined) ?? [];
  const isCatastrophic = Boolean(ev.is_catastrophic);
  const contraindication =
    (ev.contraindication_violated as Record<string, unknown> | undefined) ?? {};
  const contraPresent = Boolean(contraindication.present);

  return (
    <div className="space-y-3">
      <header className="space-y-1">
        <div className="flex items-center gap-2 font-[var(--font-display)] text-sm font-bold">
          <Pill size={14} className="text-[var(--color-blue-soft)]" />
          Decisión terapéutica
        </div>
        <p className="text-[11.5px] text-[var(--color-text-muted)]">
          Cada intervención dispara consecuencias en cascada evaluadas por el motor experto.
        </p>
      </header>

      <Input
        value={intervention}
        onChange={(e) => setIntervention(e.target.value)}
        disabled={disabled || mut.isPending}
        placeholder="Ej: Nitroglicerina SL 0.4 mg"
      />

      <div className="flex flex-wrap gap-1.5">
        {QUICK_ACTIONS.map((q) => (
          <button
            key={q}
            type="button"
            onClick={() => setIntervention(q)}
            disabled={disabled || mut.isPending}
            className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-[11px] text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-blue)]/40 hover:text-[var(--color-text-primary)] disabled:opacity-50"
          >
            {q}
          </button>
        ))}
      </div>

      <Textarea
        rows={2}
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        disabled={disabled || mut.isPending}
        placeholder="Detalles (vía, dosis, monitoreo) — opcional"
      />

      <Textarea
        rows={2}
        value={reasoning}
        onChange={(e) => setReasoning(e.target.value)}
        disabled={disabled || mut.isPending}
        placeholder="¿Por qué esta intervención? (justificación clínica)"
      />

      <div className="flex gap-2">
        <Button
          onClick={() => mut.mutate()}
          disabled={disabled || mut.isPending || intervention.length < 2}
          className="flex-1"
        >
          {mut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Pill size={14} />}
          Administrar
        </Button>
        <Button
          variant="outline"
          onClick={() => advanceMut.mutate(30)}
          disabled={disabled || advanceMut.isPending}
          title="Avanza 30 min simulados"
        >
          <Clock size={14} />
          +30min
        </Button>
      </div>

      {lastDecision && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className={
            isCatastrophic
              ? "rounded-[var(--radius-lg)] border border-[var(--color-critical)]/40 bg-[var(--color-critical)]/10 p-4 card-critical"
              : "rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)]/80 p-4"
          }
        >
          <div className="mb-2 flex items-center gap-2">
            <Badge variant={isCatastrophic ? "critical" : "info"}>
              {isCatastrophic ? (
                <>
                  <Skull size={10} /> catastrófico
                </>
              ) : (
                "Evaluación"
              )}
            </Badge>
            {contraPresent && (
              <Badge variant="critical">
                <AlertTriangle size={10} /> contraindicación
              </Badge>
            )}
          </div>

          {(ev.expected_effects as string[] | undefined)?.length && (
            <Section title="Efectos esperados">
              {(ev.expected_effects as string[]).map((e, i) => (
                <li key={i} className="text-[12.5px]">
                  ✓ {e}
                </li>
              ))}
            </Section>
          )}

          {adverse.length > 0 && (
            <Section title="Efectos adversos potenciales">
              {adverse.map((a, i) => (
                <li key={i} className="text-[12.5px]">
                  <Badge variant="urgent" size="sm">
                    {String(a.probability ?? "—")}
                  </Badge>{" "}
                  <span className="font-semibold">{String(a.effect ?? "")}</span>
                  {a.reason ? <span className="text-[var(--color-text-secondary)]">: {String(a.reason)}</span> : null}
                </li>
              ))}
            </Section>
          )}

          {resulting.length > 0 && (
            <Section title="Eventos registrados en la timeline">
              {resulting.map((e, i) => (
                <li key={i} className="text-[12.5px]">
                  <Badge
                    variant={
                      (e.severity_delta as number) >= 2
                        ? "critical"
                        : (e.severity_delta as number) <= -2
                          ? "stable"
                          : "urgent"
                    }
                    size="sm"
                  >
                    Δ {e.severity_delta as number}
                  </Badge>{" "}
                  {String(e.description ?? "")}
                </li>
              ))}
            </Section>
          )}

          {isCatastrophic && (
            <div className="mt-3 flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-critical)]/40 bg-[var(--color-critical)]/5 p-3 text-[13px] text-[var(--color-critical)]">
              <Siren size={14} />
              <span>
                Outcome clínico catastrófico. El reloj simulado fue adelantado para que
                intervengas de inmediato.
              </span>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-3">
      <div className="mb-1 font-[var(--font-mono)] text-[10px] uppercase tracking-widest text-[var(--color-text-muted)]">
        {title}
      </div>
      <ul className="space-y-1">{children}</ul>
    </div>
  );
}
